import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Check, X, Tag, Settings2 } from 'lucide-react';
import { catalogService } from '../services/catalogService';
import { api } from '../config/api';

const ProductIndustrialSection = ({
  formData,
  setFormData,
  ports = [],
  priceLists = [],
  productId = null,
  section = 'all', // 'all' | 'prices' | 'logistics'
  onPortCreated = null,
}) => {
  const [showPortModal, setShowPortModal] = useState(false);
  const [newPortName, setNewPortName] = useState('');
  const [savingPort, setSavingPort] = useState(false);
  const [portError, setPortError] = useState('');

  const handleCreatePort = async (e) => {
    if (e && e.preventDefault) { e.preventDefault(); e.stopPropagation(); }
    if (!newPortName.trim()) { setPortError('El nombre es obligatorio'); return; }
    setSavingPort(true);
    setPortError('');
    try {
      const created = await api.post('/pricing/ports/', { name: newPortName.trim() });
      setNewPortName('');
      setShowPortModal(false);
      if (onPortCreated) onPortCreated(created);
    } catch (err) {
      setPortError(err?.message || 'No se pudo crear el puerto');
    } finally {
      setSavingPort(false);
    }
  };
  // ── Precios por lista ──────────────────────────────────────────────────────
  const [productPrices, setProductPrices] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [addingRow, setAddingRow] = useState(false);
  const [newPriceListId, setNewPriceListId] = useState('');
  const [newPriceValue, setNewPriceValue] = useState('');
  const [savingNew, setSavingNew] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  const loadPrices = useCallback(async () => {
    if (!productId) return;
    setLoadingPrices(true);
    try {
      const data = await catalogService.priceLists.getProductPrices(productId);
      setProductPrices(Array.isArray(data) ? data : []);
    } catch {
      setProductPrices([]);
    } finally {
      setLoadingPrices(false);
    }
  }, [productId]);

  useEffect(() => {
    loadPrices();
  }, [loadPrices]);

  // IDs de listas ya asignadas (para no repetir en el dropdown de nueva)
  const usedListIds = productPrices.map((p) => String(p.price_list));
  const availableLists = priceLists.filter((pl) => !usedListIds.includes(String(pl.id)));

  const handleSaveNew = async () => {
    if (!newPriceListId || newPriceValue === '') return;
    setSavingNew(true);
    try {
      await catalogService.priceLists.upsertProductPrice(productId, newPriceListId, newPriceValue);
      await loadPrices();
      setAddingRow(false);
      setNewPriceListId('');
      setNewPriceValue('');
    } catch (e) {
      alert('Error al guardar el precio: ' + (e?.message || e));
    } finally {
      setSavingNew(false);
    }
  };

  const handleDelete = async (price) => {
    if (!window.confirm(`¿Eliminar precio de la lista "${price.price_list_name}"?`)) return;
    setDeletingId(price.id);
    try {
      await catalogService.priceLists.deleteProductPrice(productId, price.price_list);
      setProductPrices((prev) => prev.filter((p) => p.id !== price.id));
    } catch (e) {
      alert('Error al eliminar: ' + (e?.message || e));
    } finally {
      setDeletingId(null);
    }
  };

  const handleStartEdit = (price) => {
    setEditingItemId(price.id);
    setEditingValue(price.price_fob_unit);
  };

  const handleSaveEdit = async (price) => {
    try {
      await catalogService.priceLists.upsertProductPrice(productId, price.price_list, editingValue);
      setProductPrices((prev) =>
        prev.map((p) => (p.id === price.id ? { ...p, price_fob_unit: editingValue } : p))
      );
      setEditingItemId(null);
    } catch (e) {
      alert('Error al actualizar: ' + (e?.message || e));
    }
  };

  // ── CBM calculado ──────────────────────────────────────────────────────────
  useEffect(() => {
    const h = parseFloat(formData.master_pack_height);
    const l = parseFloat(formData.master_pack_length);
    const w = parseFloat(formData.master_pack_width);
    if (!isNaN(h) && !isNaN(l) && !isNaN(w) && h > 0 && l > 0 && w > 0) {
      const cbm = parseFloat(((h * l * w) / 1000000).toFixed(6));
      setFormData((prev) => ({ ...prev, cbm_per_ctn: cbm }));
    } else {
      setFormData((prev) => ({ ...prev, cbm_per_ctn: '' }));
    }
  }, [formData.master_pack_height, formData.master_pack_length, formData.master_pack_width]);

  return (
    <div className="space-y-4">
      {/* ── Sección Listas de Precios ───────────────────────────────────────── */}
      {(section === 'all' || section === 'prices') && <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-semibold text-gray-700">Precios por Lista</span>
            {loadingPrices && <span className="text-xs text-gray-400">Cargando...</span>}
          </div>
          {productId && (
            <button
              type="button"
              onClick={() => { setAddingRow(true); setNewPriceListId(''); setNewPriceValue(''); }}
              disabled={addingRow || availableLists.length === 0}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus className="w-3.5 h-3.5" />
              Lista de Precios
            </button>
          )}
          {!productId && (
            <span className="text-xs text-gray-400">Guardá el producto primero para asignar precios</span>
          )}
        </div>

        <div className="divide-y divide-gray-100">
          {/* Fila para agregar nuevo precio */}
          {addingRow && (() => {
            const previewPrice  = parseFloat(newPriceValue) || 0;
            const costVal       = parseFloat(formData.cost_fob) || 0;
            const profit        = previewPrice - costVal;
            const marginPct     = previewPrice > 0 ? (profit / previewPrice * 100) : 0;
            const markupPct     = costVal > 0      ? (profit / costVal  * 100) : 0;
            const hasPreview    = previewPrice > 0 && costVal > 0;
            return (
              <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 space-y-2">
                <div className="flex items-center gap-3">
                  <select
                    value={newPriceListId}
                    onChange={(e) => setNewPriceListId(e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  >
                    <option value="">Seleccionar lista...</option>
                    {availableLists.map((pl) => (
                      <option key={pl.id} value={pl.id}>
                        {pl.name} {pl.code ? `(${pl.code})` : ''} — {pl.currency || 'USD'}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    step="0.000001"
                    min="0"
                    placeholder="Precio FOB"
                    value={newPriceValue}
                    onChange={(e) => setNewPriceValue(e.target.value)}
                    className="w-36 px-3 py-2 text-sm border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  />
                  <button
                    type="button"
                    onClick={handleSaveNew}
                    disabled={savingNew || !newPriceListId || newPriceValue === ''}
                    className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors disabled:opacity-40"
                    title="Confirmar"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddingRow(false)}
                    className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Cancelar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {hasPreview && (
                  <div className="flex items-center gap-4 text-xs pl-1">
                    <span className="text-gray-500">Costo: <span className="font-mono font-semibold text-gray-700">{costVal.toFixed(2)} USD</span></span>
                    <span className="text-gray-500">Margen: <span className={`font-mono font-semibold ${marginPct >= 20 ? 'text-green-700' : marginPct >= 10 ? 'text-amber-600' : 'text-red-600'}`}>{marginPct.toFixed(1)}%</span></span>
                    <span className="text-gray-500">Ganancia: <span className={`font-mono font-semibold ${profit >= 0 ? 'text-green-700' : 'text-red-600'}`}>{profit >= 0 ? '+' : ''}{profit.toFixed(2)} USD</span></span>
                    <span className="text-gray-500">Markup: <span className="font-mono font-semibold text-gray-700">{markupPct.toFixed(1)}%</span></span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Lista de precios existentes */}
          {productPrices.length === 0 && !addingRow && (
            <div className="px-4 py-6 text-center text-sm text-gray-400">
              {productId
                ? 'Sin precios por lista asignados. Usá el botón "Lista de Precios" para agregar.'
                : 'Guardá el producto primero.'}
            </div>
          )}

          {/* Cabecera de columnas */}
          {productPrices.length > 0 && (
            <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_auto] gap-2 px-4 py-2 bg-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              <span>Lista</span>
              <span className="text-right">Precio</span>
              <span className="text-right">Costo</span>
              <span className="text-right">Margen %</span>
              <span className="text-right">Ganancia</span>
              <span className="text-right">Markup %</span>
              <span />
            </div>
          )}

          {productPrices.map((price) => {
            const priceVal  = parseFloat(editingItemId === price.id ? editingValue : price.price_fob_unit) || 0;
            const costVal   = parseFloat(formData.cost_fob) || 0;
            const profit    = priceVal - costVal;
            const marginPct = priceVal > 0   ? (profit / priceVal * 100) : 0;
            const markupPct = costVal  > 0   ? (profit / costVal  * 100) : 0;
            const hasCost   = costVal > 0;

            const marginColor = marginPct >= 20 ? 'text-green-700' : marginPct >= 10 ? 'text-amber-600' : 'text-red-600';

            return (
              <div key={price.id} className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_auto] gap-2 items-center px-4 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0">
                {/* Nombre lista */}
                <span className="text-sm font-medium text-gray-800">{price.price_list_name}</span>

                {/* Precio — editable */}
                <div className="flex items-center justify-end gap-1">
                  {editingItemId === price.id ? (
                    <>
                      <input
                        type="number"
                        step="0.000001"
                        min="0"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        className="w-24 px-2 py-1 text-sm text-right border border-blue-300 rounded-lg focus:outline-none"
                        autoFocus
                      />
                      <button type="button" onClick={() => handleSaveEdit(price)} className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors" title="Guardar">
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => setEditingItemId(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded transition-colors" title="Cancelar">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleStartEdit(price)}
                      className="text-sm font-mono font-semibold text-blue-700 hover:text-blue-900 px-2 py-0.5 rounded hover:bg-blue-50 transition-colors"
                      title="Clic para editar"
                    >
                      {priceVal.toFixed(2)} <span className="text-xs text-gray-400 font-normal">USD</span>
                    </button>
                  )}
                </div>

                {/* Costo */}
                <span className="text-sm text-right text-gray-600 font-mono">
                  {hasCost ? <>{costVal.toFixed(2)} <span className="text-xs text-gray-400">USD</span></> : <span className="text-gray-300">—</span>}
                </span>

                {/* Margen % */}
                <span className={`text-sm text-right font-semibold font-mono ${hasCost ? marginColor : 'text-gray-300'}`}>
                  {hasCost ? `${marginPct.toFixed(1)}%` : '—'}
                </span>

                {/* Ganancia */}
                <span className={`text-sm text-right font-mono ${hasCost ? (profit >= 0 ? 'text-green-700' : 'text-red-600') : 'text-gray-300'}`}>
                  {hasCost ? <>{profit >= 0 ? '+' : ''}{profit.toFixed(2)} <span className="text-xs font-normal">USD</span></> : '—'}
                </span>

                {/* Markup % */}
                <span className={`text-sm text-right font-mono text-gray-500 ${!hasCost ? 'text-gray-300' : ''}`}>
                  {hasCost ? `${markupPct.toFixed(1)}%` : '—'}
                </span>

                {/* Eliminar */}
                <button
                  type="button"
                  onClick={() => handleDelete(price)}
                  disabled={deletingId === price.id}
                  className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40"
                  title="Eliminar"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </div>}

      {(section === 'all' || section === 'logistics') && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CBM por CTN <span className="text-xs text-gray-400 font-normal">(calculado: alto × largo × ancho ÷ 1,000,000)</span>
              </label>
              <input
                type="number"
                step="0.000001"
                min="0"
                value={formData.cbm_per_ctn ?? ''}
                readOnly
                className="w-full px-4 py-2 border border-gray-200 rounded-xl bg-gray-100 cursor-not-allowed text-gray-600"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Puerto de fabrica</label>
                <button
                  type="button"
                  onClick={() => { setShowPortModal(true); setPortError(''); setNewPortName(''); }}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  <Settings2 className="w-3.5 h-3.5" />
                  Gestionar
                </button>
              </div>
              <select
                value={formData.factory_port || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, factory_port: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              >
                <option value="">Seleccionar</option>
                {ports.map((port) => (
                  <option key={port.id} value={port.id}>
                    {port.name || `Puerto #${port.id}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Puerto de embarque</label>
                <button
                  type="button"
                  onClick={() => { setShowPortModal(true); setPortError(''); setNewPortName(''); }}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  <Settings2 className="w-3.5 h-3.5" />
                  Gestionar
                </button>
              </div>
              <select
                value={formData.shipping_port || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, shipping_port: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              >
                <option value="">Seleccionar</option>
                {ports.map((port) => (
                  <option key={port.id} value={port.id}>
                    {port.name || `Puerto #${port.id}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Modal rápido: crear puerto ── */}
          {showPortModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowPortModal(false)}>
              <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-800">Nuevo Puerto</h3>
                  <button type="button" onClick={() => setShowPortModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del puerto</label>
                    <input
                      autoFocus
                      type="text"
                      value={newPortName}
                      onChange={(e) => setNewPortName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); handleCreatePort(e); } }}
                      placeholder="Ej: Ningbo, Shanghai..."
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                    {portError && <p className="mt-1 text-xs text-red-600">{portError}</p>}
                  </div>
                  <div className="flex justify-end gap-2 pt-1">
                    <button type="button" onClick={() => setShowPortModal(false)} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={handleCreatePort}
                      disabled={savingPort}
                      className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {savingPort ? 'Creando...' : 'Crear puerto'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductIndustrialSection;
